// Entry analysis — the sparkle lenses, ported from iOS's Features/Composer/EntryReflection.swift.
//
// Six readings of one piece of writing: three for a waking entry (go deeper, shadow, bright) and
// three for a dream (Jung, biological, spiritual). Each lens is a system prompt plus a closing ask,
// streamed through /api/oracle.
//
// There is no analysis endpoint to build and none was needed. The Worker's oracle handler resolves a
// persona server-side so the companion's voice can be retuned without shipping a client, but it lets
// an explicit `systemPrompt` win — and its own comment names this feature as the reason. A lens is
// per-invocation rather than per-preference, so passing the prompt is the right shape.
//
// The waking strings stay close to Swift. The dream strings used to as well, including "two or
// three short paragraphs at most" — which produced readings too thin to sit with. Dream lenses on
// the web now ask for the images one at a time. The phone is unchanged.

import { ApiError, streamSSE } from './client';
import { hasLoggedDreamDetails, type DreamDetails } from './dream-details';

export type AnalysisKind = 'deeper' | 'shadow' | 'light' | 'jung' | 'biological' | 'spiritual';

/** iOS `AnalysisKind.wakingKinds`. */
export const WAKING_KINDS: readonly AnalysisKind[] = ['deeper', 'shadow', 'light'];

/** iOS `AnalysisKind.dreamKinds` — three lenses on the same dream. */
export const DREAM_KINDS: readonly AnalysisKind[] = ['jung', 'biological', 'spiritual'];

export function kindsFor(entryKind: 'waking' | 'dream'): readonly AnalysisKind[] {
  return entryKind === 'dream' ? DREAM_KINDS : WAKING_KINDS;
}

export function isDreamKind(kind: AnalysisKind): boolean {
  return DREAM_KINDS.includes(kind);
}

interface Lens {
  /** Short label on the sparkle menu. */
  title: string;
  /** Header line inside the analysis sheet. */
  heading: string;
  /** One line under the heading, so the reader knows what they asked for before it arrives. */
  blurb: string;
  system: string;
  ask: string;
}

const BASE =
  'You are Oreia, a warm, intuitive reflective companion. You speak directly to the person, ' +
  'briefly and gently. You draw ONLY from what they actually wrote — never invent details, never ' +
  'flatter, never diagnose. Two or three short paragraphs at most.';

const DREAM_BASE =
  'You are Oreia, a warm, insightful dream interpreter speaking directly to the dreamer. ' +
  'Ground your reading in the dream they describe and the details they logged — never invent ' +
  'details, never flatter or diagnose. When you have context about their recent waking life, ' +
  'gently connect the dream to it: a dream rarely stands alone, and the most meaningful reading ' +
  'considers the life it grew out of. ' +
  'Do not summarise the whole dream in two paragraphs. Work through the images one at a time, ' +
  'in the order they appear: what each one is doing in this dream, how it sits next to the others, ' +
  'and what it may be asking. Several short paragraphs — enough that each image gets its own ' +
  'attention — then close with what the psyche seems to be working through. Stay with what they ' +
  'wrote. Do not pad. Use 4–6 short paragraphs and about 250–400 words for a rich dream; ' +
  'use fewer for a brief dream. Open with the central tension, explore the most meaningful images, ' +
  'then close with one open question. Interpretations are possibilities, not facts. ' +
  'When connecting to past entries, cite their supplied title and date. If that evidence is absent, ' +
  'do not claim a recurring pattern or a connection to their life. Do not invent citations. ' +
  'Use plain prose without markdown headings, lists, or repeated summaries.';

const JUNG_ANGLE =
  " Interpret the dream through Carl Jung's lens: archetypes, the shadow, " +
  'anima/animus, the collective unconscious, individuation, and compensation. Name which ' +
  'archetypes or symbols seem active and what the psyche may be working to integrate.';

const BIO_ANGLE =
  ' Interpret the dream through a biological / neuroscientific lens: REM sleep, ' +
  'memory consolidation, threat-simulation, emotional regulation, and how the brain reweaves ' +
  'recent experience. Stay grounded and non-mystical. Explain plausible processes rather than ' +
  'assigning fixed symbolic meanings to objects. A dream report alone cannot establish a sleep stage, ' +
  'a neurological mechanism, or a diagnosis. Distinguish general hypotheses from what this person logged.';

const SPIRIT_ANGLE =
  ' Interpret the dream through a spiritual lens: symbols as messages, intuition, ' +
  "energy, cycles, and soul growth — gentle and open, honoring the dreamer's own meaning " +
  'rather than imposing any dogma. Offer a spiritual interpretation as an optional metaphor; ' +
  'do not assert supernatural messages, predictions, or external forces as facts.';

const SYMBOL_BASE =
  'You are Oreia, a warm, insightful dream interpreter speaking directly to the dreamer. ' +
  'Ground your reading in the dream they describe — never invent details, never flatter or diagnose. ' +
  'The dreamer has asked you to sit with one image from this dream, not the whole of it. ' +
  'Two or three short paragraphs: what this image is doing here, what it may mean, and one question ' +
  'worth sitting with. Do not retell the rest of the dream. Stay with what they wrote.';

const ANGLES: Record<'jung' | 'biological' | 'spiritual', string> = {
  jung: JUNG_ANGLE,
  biological: BIO_ANGLE,
  spiritual: SPIRIT_ANGLE,
};

const LENSES: Record<AnalysisKind, Lens> = {
  deeper: {
    title: 'Go deeper',
    heading: 'Going deeper',
    blurb: 'One honest observation and one question worth sitting with.',
    system:
      BASE +
      " Reflect their entry back to them and take it one layer deeper: offer one honest " +
      "observation and one open question that helps them see what's underneath.",
    ask: 'Reflect this back to me and take me one layer deeper.',
  },
  shadow: {
    title: 'Shadow patterns',
    heading: 'Shadow patterns',
    blurb: 'What you may be turning away from, named gently.',
    system:
      BASE +
      ' Name, gently, any shadow patterns you notice — avoidances, fears, defenses, or ' +
      'where they may be turning away from something. Be compassionate and honest, never harsh.',
    ask: 'What shadow patterns do you notice here?',
  },
  light: {
    title: 'Bright patterns',
    heading: 'Bright patterns',
    blurb: "What's working, brave, wise or kind in what you wrote.",
    system:
      BASE +
      " Name the strengths and healthy patterns you notice — what's working, what's brave, " +
      'wise, or kind here. Be specific and grounded in their words, never empty praise.',
    ask: 'What strengths and bright patterns do you notice here?',
  },
  jung: {
    title: 'Through Carl Jung',
    heading: 'A Jungian reading',
    blurb: 'Archetypes, the shadow, and what the psyche is working to integrate — image by image.',
    system: DREAM_BASE + JUNG_ANGLE,
    ask: "Interpret this dream through Carl Jung's perspective. Work through the images one at a time.",
  },
  biological: {
    title: 'Biological view',
    heading: 'The biological view',
    blurb: 'Possible links to memory, emotion, and sleep — grounded hypotheses, not a diagnosis.',
    system: DREAM_BASE + BIO_ANGLE,
    ask: 'Interpret this dream through a biological and neurological perspective. Work through the images one at a time.',
  },
  spiritual: {
    title: 'Spiritual view',
    heading: 'A spiritual reading',
    blurb: 'Symbols as messages, intuition, cycles, and soul growth — image by image.',
    system: DREAM_BASE + SPIRIT_ANGLE,
    ask: 'Interpret this dream through a spiritual perspective. Work through the images one at a time.',
  },
};

export function lens(kind: AnalysisKind): Lens {
  return LENSES[kind];
}

/** How much of the story the model is asked to read. iOS's `String(trimmed.prefix(3000))`. */
const STORY_LIMIT = 3000;

/**
 * A compact summary of everything logged on the Symbols / Details / Lucidity pages.
 *
 * Mirrors iOS's `OblivionPayload.reflectionSummary`, so a reading considers the whole dream record
 * rather than only the prose. Empty when the pages are untouched — sending a block of defaults would
 * have the model interpret "neutral, first person, lucidity 2" as things the dreamer reported.
 */
export function detailsSummary(details: DreamDetails): string {
  if (!hasLoggedDreamDetails(details)) return '';

  const lines: string[] = [];
  const line = (label: string, values: string[]) => {
    if (values.length > 0) lines.push(`${label}: ${values.join(', ')}`);
  };

  line('Setting', details.setting);
  line('Atmosphere', details.atmosphere);
  line('Emotions', details.emotions);
  line('Key elements', details.elements);
  line('Characters', details.characters);
  line('Themes', details.themes);
  line('Actions', details.actions);
  line('Dream nature', details.dreamNature);
  line('Dreamed as', details.dreamedAs);
  if (details.happened) lines.push(`What happened: ${details.happened}`);
  lines.push(
    `Tone: ${details.tone}; lucidity ${details.lucidity}/5; vividness ${details.vividness}/5; ` +
      `perspective: ${details.perspective}`,
  );
  if (details.recurring) lines.push('This is a recurring dream.');
  if (details.sleepQuality > 0) lines.push(`Sleep quality: ${details.sleepQuality}/5`);

  return lines.join('\n');
}

/** As many chips as stay scannable under a reading. Past that it is a list, not a choice. */
const SYMBOL_CHIP_CAP = 8;

/**
 * The images a dream reading can sit with one at a time.
 *
 * Key elements first — those are what the dreamer named as the dream's pieces — then setting,
 * characters, actions, then any feed chips they typed. Deduped case-insensitively so "Forest" and
 * "the forest" do not become two readings of the same thing.
 */
export function analysisSymbols(details: DreamDetails, extra: string[] = []): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const raw of [
    ...details.elements,
    ...details.setting,
    ...details.characters,
    ...details.actions,
    ...extra,
  ]) {
    const value = raw.trim().replace(/\s+/g, ' ');
    if (value.length < 2 || value.length > 28) continue;
    const key = value.toLowerCase().replace(/^(?:a|an|the)\s+/, '');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(value);
    if (out.length === SYMBOL_CHIP_CAP) break;
  }

  return out;
}

export function buildAnalysisPrompt(
  kind: AnalysisKind,
  body: string,
  details = '',
  symbol?: string,
): string {
  const snippet = body.trim().slice(0, STORY_LIMIT);
  const label = isDreamKind(kind) ? 'Here is a dream I had' : 'Here is a journal entry I wrote';

  let out = `${label}:\n\n"${snippet}"`;
  if (details) out += `\n\nDetails I logged about this dream:\n${details}`;
  if (symbol) {
    out += `\n\nFocus only on this image from the dream: "${symbol}". ${LENSES[kind].ask.replace(
      / Work through the images one at a time\.?$/,
      '',
    )} Stay with that image.`;
  } else {
    out += `\n\n${LENSES[kind].ask}`;
  }
  return out;
}

/**
 * What to say when the reading cannot be fetched.
 *
 * The two gate codes get their own words because they are not failures — the person has hit a limit
 * and there is something they can do about it. Same mapping as `EntryAnalysisService`.
 */
export function analysisErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 402) {
      return 'Oreia needs a moment to recharge. Your daily AI credits renew at midnight, or you can expand your capacity to continue without pause.';
    }
    if (err.status === 429) {
      return "You've reached your daily soft cap for AI insights. Please try again tomorrow.";
    }
  }
  return "I couldn't reach a reflection just now. Try again in a moment.";
}

/** Shown when the stream succeeds but says nothing — better than an empty sheet. */
export const EMPTY_ANALYSIS_TEXT =
  "I'm still sitting with this. Tell me a little more and we can look again.";

export interface AnalyzeOptions {
  kind: AnalysisKind;
  body: string;
  /** The dream's structured pages, summarised. Omitted for a waking entry. */
  details?: string;
  /** When set, a reading of this image only — not the whole dream. */
  symbol?: string;
  onToken(token: string): void;
  signal?: AbortSignal;
}

export async function analyzeEntry(options: AnalyzeOptions): Promise<void> {
  const symbol = options.symbol?.trim();
  const focused = Boolean(symbol) && isDreamKind(options.kind);
  const angle = focused ? ANGLES[options.kind as 'jung' | 'biological' | 'spiritual'] : '';

  await streamSSE(
    '/api/oracle',
    {
      prompt: buildAnalysisPrompt(options.kind, options.body, options.details ?? '', symbol),
      systemPrompt: focused ? SYMBOL_BASE + angle : LENSES[options.kind].system,
    },
    options.onToken,
    options.signal,
  );
}
